import {
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../../components_catalyst/dropdown';
import {
  BeakerIcon,
  CommandLineIcon,
  PlayIcon,
  Bars4Icon,
  PlusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import { DragHandle } from '../../components_misc/DragHandle';
import { CheckIcon } from '@heroicons/react/20/solid';
import { CrosshairsIcon } from '../../components_misc/CrosshairsIcon';
import { DependenciesIcon } from '../../components_misc/DependenciesIcon';
import {
  StatusCellDisplayIcon,
  StatusCellDisplayMode,
} from '../../components_misc/StatusCellDisplayIcon';

export interface Map3ControlsDropdownMenuProps {
  // Feature flags from milestoneMap
  featureFlags?: {
    enableTreeView?: boolean;
    enableCrosshairs?: boolean;
    enablePlaylists?: boolean;
    enableRelatives?: boolean;
    enableAddExistingObjectives?: boolean;
    enableHierarchyZoom?: boolean;
  };
  // Current toggle states (for showing active state)
  toggleStates: {
    showStatusCodes: boolean;
    statusCellDisplayMode: StatusCellDisplayMode;
    enableCrosshairs: boolean;
    enablePlaylistMode: boolean;
    enableHierarchyZoomMode: boolean;
    isRelativesView: boolean;
    enableAddExistingObjectives: boolean;
    enableTreeView: boolean;
    enableSidebarTreeView: boolean;
  };
  // Handler functions
  handlers: {
    onToggleStatusCodes: () => void;
    onSetStatusCellDisplayMode: (mode: StatusCellDisplayMode) => void;
    onToggleCrosshairs: () => void;
    onSetInteractionMode: (mode: 'drag' | 'playlist' | 'zoom') => void;
    onToggleRelativesView: () => void;
    onToggleAddExistingObjectives: () => void;
    onToggleTreeView: () => void;
    onToggleSidebarTreeView: () => void;
  };
}

// Shared class for all dropdown items - overrides Catalyst grid with flex layout
// Must use !flex to override the default grid display from Catalyst DropdownItem
const itemClassName = "!flex items-center gap-3 px-4 py-2 !rounded-none";

export function Map3ControlsDropdownMenu({
  featureFlags,
  toggleStates,
  handlers,
}: Map3ControlsDropdownMenuProps) {
  const { isRelativesView } = toggleStates;

  return (
    <DropdownMenu aria-label="Map controls menu" className="!w-auto !min-w-max">
      {/* Status codes - always visible */}
      <DropdownItem onClick={handlers.onToggleStatusCodes} className={itemClassName}>
        <div className="w-5 flex-shrink-0">
          {toggleStates.showStatusCodes && (
            <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
          )}
        </div>
        <CommandLineIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        Status codes
      </DropdownItem>

      {/* Crosshair Highlighter - hidden in relatives view */}
      {featureFlags?.enableCrosshairs && !isRelativesView && (
        <DropdownItem onClick={handlers.onToggleCrosshairs} className={itemClassName}>
          <div className="w-5 flex-shrink-0">
            {toggleStates.enableCrosshairs && (
              <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
            )}
          </div>
          <CrosshairsIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          Crosshair Highlighter
        </DropdownItem>
      )}

      {/* Status cell display mode options - hidden in relatives view */}
      {!isRelativesView && (
        <>
          <DropdownDivider />

          <DropdownItem
            onClick={() => handlers.onSetStatusCellDisplayMode('status')}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {toggleStates.statusCellDisplayMode === 'status' && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <StatusCellDisplayIcon mode="status" className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Status only
          </DropdownItem>

          <DropdownItem
            onClick={() => handlers.onSetStatusCellDisplayMode('status_results')}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {toggleStates.statusCellDisplayMode === 'status_results' && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <StatusCellDisplayIcon
              mode="status_results"
              className="h-5 w-5 flex-shrink-0"
              aria-hidden="true"
            />
            Status & results
          </DropdownItem>

          <DropdownItem
            onClick={() => handlers.onSetStatusCellDisplayMode('status_results_comments')}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {toggleStates.statusCellDisplayMode === 'status_results_comments' && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <StatusCellDisplayIcon
              mode="status_results_comments"
              className="h-5 w-5 flex-shrink-0"
              aria-hidden="true"
            />
            Status, results, comments
          </DropdownItem>

          <DropdownItem
            onClick={() => handlers.onSetStatusCellDisplayMode('status_results2_comments')}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {toggleStates.statusCellDisplayMode === 'status_results2_comments' && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <StatusCellDisplayIcon
              mode="status_results2_comments"
              className="h-5 w-5 flex-shrink-0"
              aria-hidden="true"
            />
            Status, results delivered, comments
          </DropdownItem>
        </>
      )}

      {/* Interaction mode section */}
      {/* In relatives view: only show Zoom mode as a toggle (if enabled) */}
      {/* In normal view: show all interaction modes (if features enabled) */}
      {isRelativesView ? (
        // Relatives view: Zoom mode as toggle (click to enable/disable time zoom controls)
        featureFlags?.enableHierarchyZoom && (
          <>
            <DropdownDivider />
            <DropdownItem
              onClick={() => handlers.onSetInteractionMode(
                toggleStates.enableHierarchyZoomMode ? 'drag' : 'zoom'
              )}
              className={itemClassName}
            >
              <div className="w-5 flex-shrink-0">
                {toggleStates.enableHierarchyZoomMode && (
                  <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                )}
              </div>
              <MagnifyingGlassPlusIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              Zoom mode
            </DropdownItem>
          </>
        )
      ) : (
        // Normal view: show all interaction modes
        (featureFlags?.enablePlaylists || featureFlags?.enableHierarchyZoom) && (
          <>
            <DropdownDivider />
            {/* Drag and drop mode - the default */}
            <DropdownItem
              onClick={() => handlers.onSetInteractionMode('drag')}
              className={itemClassName}
            >
              <div className="w-5 flex-shrink-0">
                {!toggleStates.enablePlaylistMode && !toggleStates.enableHierarchyZoomMode && (
                  <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                )}
              </div>
              <DragHandle className="h-4 w-4 flex-shrink-0 fill-current" aria-hidden="true" />
              Drag and drop mode
            </DropdownItem>

            {featureFlags?.enablePlaylists && (
              <DropdownItem
                onClick={() => handlers.onSetInteractionMode('playlist')}
                className={itemClassName}
              >
                <div className="w-5 flex-shrink-0">
                  {toggleStates.enablePlaylistMode && (
                    <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                  )}
                </div>
                <PlayIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                Playlist mode
              </DropdownItem>
            )}

            {featureFlags?.enableHierarchyZoom && (
              <DropdownItem
                onClick={() => handlers.onSetInteractionMode('zoom')}
                className={itemClassName}
              >
                <div className="w-5 flex-shrink-0">
                  {toggleStates.enableHierarchyZoomMode && (
                    <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                  )}
                </div>
                <MagnifyingGlassPlusIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                Zoom mode
              </DropdownItem>
            )}
          </>
        )
      )}

      {/* Dependencies - always visible when feature enabled */}
      {featureFlags?.enableRelatives && (
        <>
          <DropdownDivider />
          <DropdownItem onClick={handlers.onToggleRelativesView} className={itemClassName}>
            <div className="w-5 flex-shrink-0">
              {toggleStates.isRelativesView && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <DependenciesIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Dependencies
          </DropdownItem>
        </>
      )}

      {/* Add objectives options - hidden in relatives view */}
      {featureFlags?.enableAddExistingObjectives && !isRelativesView && (
        <>
          <DropdownDivider />
          {/* Add objectives radio-like group */}
          <DropdownItem
            onClick={() => {
              if (toggleStates.enableAddExistingObjectives) {
                handlers.onToggleAddExistingObjectives();
              }
            }}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {!toggleStates.enableAddExistingObjectives && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <PlusIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Add new objectives
          </DropdownItem>

          <DropdownItem
            onClick={() => {
              if (!toggleStates.enableAddExistingObjectives) {
                handlers.onToggleAddExistingObjectives();
              }
            }}
            className={itemClassName}
          >
            <div className="w-5 flex-shrink-0">
              {toggleStates.enableAddExistingObjectives && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <Bars4Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Add existing objectives
          </DropdownItem>
        </>
      )}

      {/* Tree view options - hidden in relatives view */}
      {featureFlags?.enableTreeView && !isRelativesView && (
        <>
          <DropdownDivider />
          <DropdownItem onClick={handlers.onToggleTreeView} className={itemClassName}>
            <div className="w-5 flex-shrink-0">
              {toggleStates.enableTreeView && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <BeakerIcon className="h-5 w-5 flex-shrink-0 fill-pink-400" aria-hidden="true" />
            Toggle full tree view
          </DropdownItem>

          <DropdownItem onClick={handlers.onToggleSidebarTreeView} className={itemClassName}>
            <div className="w-5 flex-shrink-0">
              {toggleStates.enableSidebarTreeView && (
                <CheckIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
              )}
            </div>
            <BeakerIcon className="h-5 w-5 flex-shrink-0 fill-cyan-500" aria-hidden="true" />
            Toggle sidebar tree view
          </DropdownItem>
        </>
      )}
    </DropdownMenu>
  );
}
